import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BusproAdminLiveBookingsApi } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { SeatPickerLabelPipe } from '@buspro/web/shared';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-live-booking-modal',
  standalone: true,
  imports: [SeatPickerLabelPipe, CommonModule, ReactiveFormsModule, ErrorDisplayComponent, CurrencyPipe],
  templateUrl: './live-booking-modal.component.html',
})
export class LiveBookingModalComponent implements OnChanges {
  @Input() selectedBus: any = null;
  @Input() bookingLocationId!: string;
  @Input() bookingLocationType!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() bookingSuccess = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private logger = inject(NGXLogger);
  private formErrorHandler = useFormErrorHandler();
  private api = inject(BusproAdminLiveBookingsApi);

  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;
  availableSeats = signal<any[]>([]);

  bookingForm: FormGroup = this.fb.group({
    seatId: ['', Validators.required],
    customerName: ['', Validators.required],
    customerPhone: ['', Validators.required],
    destinationTerminalId: ['', Validators.required],
    paymentMethod: ['CASH', Validators.required],
    fare: [0, Validators.required],
  });

  selectedFare = signal(0);

  bookSeatMutation = injectMutation(() => ({
    mutationFn: (data: any) =>
      lastValueFrom(
        this.api.busproManagedLiveBookingsControllerBookSeat({
          body: {
            operationId: this.selectedBus?.operationId || '',
            seatId: data.seatId,
            customerName: data.customerName,
            customerPhone: data.customerPhone,
            destinationTerminalId: data.destinationTerminalId,
            paymentMethod: data.paymentMethod,
            fare: data.fare,
            bookingLocationId: this.bookingLocationId,
            bookingLocationType: this.bookingLocationType as 'TERMINAL' | 'BUS_STOP' | 'STANDARD_PLACE',
          },
        }),
      ),
    onSuccess: () => {
      this.clearErrors();
      this.bookingSuccess.emit();
      this.close();
    },
    onError: (error: unknown) => {
      this.logger.error('Booking failed:', error);
      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'live-booking',
      });
      this.showError.set(true);
      this.errorDetails = result.problemDetail;
    },
  }));

  ngOnChanges(): void {
    if (this.selectedBus && this.visible) {
      this.loadAvailableSeats();
      this.bookingForm.patchValue({
        destinationTerminalId: this.selectedBus.possibleDestinations?.[0]?.terminalId || '',
        fare: this.selectedBus.possibleDestinations?.[0]?.fare || 0,
      });
      this.updateSelectedFare();
    }
  }

  private loadAvailableSeats(): void {
    // TODO: Load available seats from operation
    // For now, create a simple list
    const seats: any[] = [];
    if (this.selectedBus) {
      for (let i = 1; i <= this.selectedBus.totalSeats; i++) {
        seats.push({ id: `seat-${i}`, seatNumber: i, available: true });
      }
    }
    this.availableSeats.set(seats);
  }

  onDestinationChange(): void {
    this.updateSelectedFare();
  }

  private updateSelectedFare(): void {
    const destId = this.bookingForm.get('destinationTerminalId')?.value;
    const dest = this.selectedBus?.possibleDestinations?.find((d: any) => d.terminalId === destId);
    if (dest) {
      this.selectedFare.set(dest.fare);
      this.bookingForm.patchValue({ fare: dest.fare });
    }
  }

  onSubmit(): void {
    if (this.bookingForm.valid && this.selectedBus) {
      this.bookSeatMutation.mutate(this.bookingForm.value);
    }
  }

  onRetry(): void {
    this.clearErrors();
    this.onSubmit();
  }

  onDismissError(): void {
    this.clearErrors();
  }

  private clearErrors(): void {
    this.showError.set(false);
    this.errorDetails = null;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.bookingForm.reset();
  }
}
